/** How a revision's field and values read on a deal screen. */

const PERCENT_FIELDS = new Set(['ltv', 'occupancy', 'cap_rate', 'debt_yield', 'ltc'])

// Catalogue percent fields the intake stores on the 0-100 scale (`pct100` in
// backend parsing/dossier.py): the lender's floor and covenant limits, the
// stabilized and exit ratios, and the equity returns. They print with a % as
// stored — a 0.5% floor is 0.5, not a fraction to rescale.
const PERCENT100_FIELDS = new Set([
  'floor', 'covenant_max_ltv', 'covenant_min_debt_yield', 'ltv_stabilized', 'debt_yield_stabilized',
  'exit_cap', 'cap_rate_stabilized', 'expense_ratio', 'breakeven_occupancy', 'cash_on_cash',
  'levered_irr', 'unlevered_irr', 'preferred_return', 'irr_hurdle', 'promote', 'yield_on_cost',
])

const BPS_FIELDS = new Set(['origination_fee_bps', 'exit_fee_bps'])

const MULTIPLE_FIELDS = new Set(['dscr', 'covenant_min_dscr'])

const ACRONYMS = new Set(['ltv', 'ltc', 'noi', 'ncf', 'dscr', 'sf'])

export function revisionLabel(field: string | null | undefined): string {
  const words = String(field ?? '').replace(/_bps$/, '').replace(/_/g, ' ').trim().split(/\s+/).filter(Boolean)
  if (!words.length) return 'A figure'
  const text = words.map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w)).join(' ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** A revision's value as the desk reads it. Fractions stored for percent
 *  fields print as percents; amounts in dollars print in millions. */
export function fmtRevisionValue(field: string, value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (PERCENT100_FIELDS.has(field)) {
      return `${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`
    }
    if (BPS_FIELDS.has(field)) {
      return `${value.toLocaleString('en-US', { maximumFractionDigits: 0 })} bps`
    }
    if (MULTIPLE_FIELDS.has(field)) return `${value.toFixed(2)}x`
    if (PERCENT_FIELDS.has(field)) {
      const pct = Math.abs(value) <= 1.5 ? value * 100 : value
      return `${pct.toLocaleString('en-US', { maximumFractionDigits: 2 })}%`
    }
    if (Math.abs(value) >= 100_000) {
      return `$${(value / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 2 })}M`
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 4 })
  }
  if (value == null || value === '') return '—'
  return String(value)
}

